export function normalizeSpaces(value = "") {
  return String(value).replace(/\s+/g, " ").trim();
}

export function toTitleCase(value = "") {
  return normalizeSpaces(value)
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function normalizePhone(value = "") {
  return String(value).replace(/\D/g, "");
}

export function formatPhoneForDisplay(value = "") {
  const digits = normalizePhone(value).slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

export function buildFullName(firstName = "", lastName = "") {
  return normalizeSpaces(`${firstName} ${lastName}`);
}

export function buildFullNameLower(firstName = "", lastName = "") {
  return buildFullName(firstName, lastName).toLowerCase();
}

export function calculateAgeFromDob(dob = "") {
  if (!dob) return "";
  const parsed = new Date(dob);
  if (Number.isNaN(parsed.getTime())) return "";

  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  const dayDiff = today.getDate() - parsed.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 ? String(age) : "";
}

export function formatDateForDisplay(value) {
  if (!value) return "—";

  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return d.toLocaleDateString();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleDateString();
}

export function formatDateTimeForDisplay(value) {
  if (!value) return "—";

  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return d.toLocaleString();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleString();
}

export function getPatientAddressDisplay(patient = {}) {
  const street = patient.streetAddress || "";
  const city = patient.city || "";
  const state = patient.state || "";
  const zip = patient.zip || "";

  const cityState = [city, state].filter(Boolean).join(", ");
  const line2 = [cityState, zip].filter(Boolean).join(" ");
  return [street, line2].filter(Boolean).join(", ") || patient.address || "—";
}

export function getPatientSearchText(patient = {}) {
  return [
    patient.fullName,
    patient.fullNameLower,
    patient.firstName,
    patient.lastName,
    patient.phone,
    patient.phoneDigitsOnly,
    patient.dob,
    patient.email,
    patient.address,
    patient.streetAddress,
    patient.city,
    patient.state,
    patient.zip,
    patient.gender,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function sortPatients(a, b) {
  const aTime =
    a?.lastEncounterAt?.toMillis?.() ??
    a?.updatedAt?.toMillis?.() ??
    a?.createdAt?.toMillis?.() ??
    0;

  const bTime =
    b?.lastEncounterAt?.toMillis?.() ??
    b?.updatedAt?.toMillis?.() ??
    b?.createdAt?.toMillis?.() ??
    0;

  return bTime - aTime;
}

export function findDuplicateMatches(patients = [], form = {}) {
  const firstName = normalizeSpaces(form.firstName).toLowerCase();
  const lastName = normalizeSpaces(form.lastName).toLowerCase();
  const dob = String(form.dob || "").trim();
  const phoneDigitsOnly = normalizePhone(form.phone);

  const streetAddress = normalizeSpaces(form.streetAddress).toLowerCase();
  const city = normalizeSpaces(form.city).toLowerCase();
  const state = normalizeSpaces(form.state).toLowerCase();
  const zip = normalizeSpaces(form.zip).toLowerCase();

  const addressInput = normalizeSpaces(
    [streetAddress, city, state, zip].filter(Boolean).join(" ")
  ).toLowerCase();

  if (!firstName && !lastName && !dob && !phoneDigitsOnly && !addressInput) {
    return [];
  }

  const scored = patients
    .map((patient) => {
      let score = 0;
      let matchCount = 0;

      const patientFirstName = normalizeSpaces(patient.firstName).toLowerCase();
      const patientLastName = normalizeSpaces(patient.lastName).toLowerCase();
      const patientDob = String(patient.dob || "").trim();
      const patientPhone = String(patient.phoneDigitsOnly || normalizePhone(patient.phone)).trim();

      const patientAddress = normalizeSpaces(
        [
          patient.streetAddress,
          patient.city,
          patient.state,
          patient.zip,
          patient.address,
        ]
          .filter(Boolean)
          .join(" ")
      ).toLowerCase();

      if (firstName && patientFirstName === firstName) {
        score += 10;
        matchCount += 1;
      }

      if (lastName && patientLastName === lastName) {
        score += 15;
        matchCount += 1;
      }

      if (dob && patientDob === dob) {
        score += 40;
        matchCount += 1;
      }

      if (phoneDigitsOnly && patientPhone && patientPhone === phoneDigitsOnly) {
        score += 50;
        matchCount += 1;
      }

      if (addressInput && patientAddress && patientAddress.includes(addressInput)) {
        score += 25;
        matchCount += 1;
      }

      return { patient, score, matchCount };
    })
    .filter((row) => row.matchCount >= 2)
    .sort((a, b) => b.score - a.score || sortPatients(a.patient, b.patient))
    .slice(0, 8)
    .map((row) => row.patient);

  return scored;
}